import { api } from './api';

export interface PaqueteTrazabilidad {
  id: number;
  paqueteId: number;
  estadoAnterior?: string;
  estadoNuevo: string;
  ubicacion?: string;
  sucursalId?: number;
  usuarioId: number;
  fecha: string;
  notas?: string;
  foto?: string;
  sucursal?: {
    id: number;
    name: string;
  };
  usuario?: {
    id: number;
    name: string;
    email: string;
  };
  createdAt?: string;
}

export interface PaqueteServicio {
  id: number;
  paqueteId: number;
  servicioId: number;
  cantidad: number;
  precioUnitario: number;
  montoTotal: number;
  usuarioId: number;
  fecha: string;
  notas?: string;
  servicio?: {
    id: number;
    nombre: string;
  };
  usuario?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface Paquete {
  id: number;
  numeroTracking: string;
  codigoBarras?: string;
  tenantId: number;
  sucursalId: number;
  tipo: 'interno' | 'externo';
  estado:
    | 'registrado'
    | 'en_almacen'
    | 'en_transito'
    | 'entregado'
    | 'devuelto'
    | 'cancelado'
    | 'recibido_centro'
    | 'en_servicio'
    | 'preparado_salida'
    | 'despachado';
  clienteId?: number;
  transferenciaId?: number;
  numeroGuiaEntrada?: string;
  paqueteriaEntrada?: string;
  numeroGuiaSalida?: string;
  paqueteriaSalida?: string;
  fechaEntrada: string;
  fechaSalidaEstimada?: string;
  fechaSalidaReal?: string;
  diasAlmacenaje: number;
  montoAlmacenaje: number;
  peso?: number;
  dimensiones?: string;
  valorDeclarado?: number;
  remitenteNombre: string;
  remitenteTelefono?: string;
  remitenteEmail?: string;
  remitenteDireccion?: string;
  destinatarioNombre: string;
  destinatarioTelefono: string;
  destinatarioEmail?: string;
  destinatarioDireccion: string;
  ubicacionActual?: string;
  fotos?: string[];
  documentos?: string[];
  notas?: string;
  observaciones?: string;
  usuarioRegistroId: number;
  usuarioSalidaId?: number;
  sucursal?: {
    id: number;
    name: string;
  };
  cliente?: {
    id: number;
    name: string;
    email?: string;
    telefono?: string;
  };
  transferencia?: {
    id: number;
    estado: string;
  };
  usuarioRegistro?: {
    id: number;
    name: string;
    email: string;
  };
  usuarioSalida?: {
    id: number;
    name: string;
    email: string;
  };
  trazabilidad?: PaqueteTrazabilidad[];
  createdAt?: string;
  updatedAt?: string;
}

export interface PaqueteCreate {
  codigoBarras?: string;
  sucursalId: number;
  tipo?: 'interno' | 'externo';
  clienteId?: number;
  transferenciaId?: number;
  peso?: number;
  dimensiones?: string;
  valorDeclarado?: number;
  remitenteNombre: string;
  remitenteTelefono?: string;
  remitenteEmail?: string;
  remitenteDireccion?: string;
  destinatarioNombre: string;
  destinatarioTelefono: string;
  destinatarioEmail?: string;
  destinatarioDireccion: string;
  ubicacionActual?: string;
  fotos?: string[];
  documentos?: string[];
  notas?: string;
  fechaSalidaEstimada?: string;
  numeroGuiaEntrada?: string;
  paqueteriaEntrada?: string;
}

export interface PaqueteUpdate {
  peso?: number;
  dimensiones?: string;
  valorDeclarado?: number;
  remitenteNombre?: string;
  remitenteTelefono?: string;
  remitenteEmail?: string;
  remitenteDireccion?: string;
  destinatarioNombre?: string;
  destinatarioTelefono?: string;
  destinatarioEmail?: string;
  destinatarioDireccion?: string;
  ubicacionActual?: string;
  fotos?: string[];
  documentos?: string[];
  notas?: string;
  observaciones?: string;
  fechaSalidaEstimada?: string;
}

export interface CambiarEstadoPaquete {
  estado:
    | 'registrado'
    | 'en_almacen'
    | 'en_transito'
    | 'entregado'
    | 'devuelto'
    | 'cancelado'
    | 'recibido_centro'
    | 'en_servicio'
    | 'preparado_salida'
    | 'despachado';
  ubicacion?: string;
  sucursalId?: number;
  notas?: string;
  foto?: string;
}

export interface DespacharPaquetePayload {
  numeroGuiaSalida: string;
  paqueteriaSalida: string;
  sucursalDestinoId?: number;
  notas?: string;
}

export interface AgregarServicioPaquetePayload {
  servicioId: number;
  cantidad?: number;
  notas?: string;
}

export interface EstadisticasPaquetes {
  total: number;
  porEstado: Array<{
    estado: string;
    cantidad: number;
  }>;
  porTipo: Array<{
    tipo: string;
    cantidad: number;
  }>;
  promedioDias: number;
  paquetesAntiguos: number;
}

export interface PaginatedResponse<T> {
  data: {
    [key: string]: T[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const paqueteService = {
  getAll: async (
    page = 1,
    limit = 20,
    estado?: string,
    tipo?: string,
    sucursalId?: number,
    clienteId?: number,
    numeroTracking?: string,
    codigoBarras?: string,
    diasMinimos?: number
  ): Promise<PaginatedResponse<Paquete>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (estado) params.append('estado', estado);
    if (tipo) params.append('tipo', tipo);
    if (sucursalId) params.append('sucursalId', sucursalId.toString());
    if (clienteId) params.append('clienteId', clienteId.toString());
    if (numeroTracking) params.append('numeroTracking', numeroTracking);
    if (codigoBarras) params.append('codigoBarras', codigoBarras);
    if (diasMinimos) params.append('diasMinimos', diasMinimos.toString());
    
    const response = await api.get<PaginatedResponse<Paquete>>(`/paquetes?${params}`);
    return response.data;
  },

  getById: async (id: number): Promise<{ data: { paquete: Paquete } }> => {
    const response = await api.get<{ data: { paquete: Paquete } }>(`/paquetes/${id}`);
    return response.data;
  },

  buscar: async (tracking?: string, codigoBarras?: string): Promise<{ data: { paquete: Paquete } }> => {
    const params = new URLSearchParams();
    if (tracking) params.append('tracking', tracking);
    if (codigoBarras) params.append('codigoBarras', codigoBarras);
    
    const response = await api.get<{ data: { paquete: Paquete } }>(`/paquetes/buscar?${params}`);
    return response.data;
  },

  create: async (data: PaqueteCreate): Promise<{ data: { paquete: Paquete } }> => {
    const response = await api.post<{ data: { paquete: Paquete } }>('/paquetes', data);
    return response.data;
  },

  update: async (id: number, data: PaqueteUpdate): Promise<{ data: { paquete: Paquete } }> => {
    const response = await api.put<{ data: { paquete: Paquete } }>(`/paquetes/${id}`, data);
    return response.data;
  },

  cambiarEstado: async (id: number, data: CambiarEstadoPaquete): Promise<{ data: { paquete: Paquete } }> => {
    const response = await api.post<{ data: { paquete: Paquete } }>(`/paquetes/${id}/estado`, data);
    return response.data;
  },

  getTrazabilidad: async (id: number): Promise<{ data: { trazabilidad: PaqueteTrazabilidad[] } }> => {
    const response = await api.get<{ data: { trazabilidad: PaqueteTrazabilidad[] } }>(`/paquetes/${id}/trazabilidad`);
    return response.data;
  },

  getEstadisticas: async (sucursalId?: number): Promise<{ data: EstadisticasPaquetes }> => {
    const params = new URLSearchParams();
    if (sucursalId) params.append('sucursalId', sucursalId.toString());
    
    const response = await api.get<{ data: EstadisticasPaquetes }>(`/paquetes/estadisticas?${params}`);
    return response.data;
  },

  getServicios: async (id: number): Promise<{ data: { servicios: PaqueteServicio[] } }> => {
    const response = await api.get<{ data: { servicios: PaqueteServicio[] } }>(`/paquetes/${id}/servicios`);
    return response.data;
  },

  agregarServicio: async (
    id: number,
    data: AgregarServicioPaquetePayload
  ): Promise<{ data: { servicio: PaqueteServicio } }> => {
    const response = await api.post<{ data: { servicio: PaqueteServicio } }>(`/paquetes/${id}/servicios`, data);
    return response.data;
  },

  despachar: async (id: number, data: DespacharPaquetePayload): Promise<{ data: { paquete: Paquete } }> => {
    const response = await api.post<{ data: { paquete: Paquete } }>(`/paquetes/${id}/despachar`, data);
    return response.data;
  }
};

